import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import SEO from "@/components/site/SEO";
import { useReveal } from "@/hooks/useReveal";
import { ALPHABET, activeLetters, entries, type GlossaryCategory } from "@/lib/glossary";

const CATEGORIES: GlossaryCategory[] = [
  "Altersvorsorge",
  "Investments",
  "Steuern",
  "Versicherung",
  "Förderungen",
  "Vermögensaufbau",
];

export default function Glossar() {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<GlossaryCategory | "Alle">("Alle");
  useReveal(`${query}|${activeCategory}`);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = entries.filter((e) => {
      const matchesCategory = activeCategory === "Alle" || e.category === activeCategory;
      if (!matchesCategory) return false;
      if (!q) return true;
      return (
        e.term.toLowerCase().includes(q) ||
        e.short.toLowerCase().includes(q) ||
        e.category.toLowerCase().includes(q)
      );
    });
    return filtered.reduce<Record<string, typeof entries>>((acc, entry) => {
      const letter = entry.term.charAt(0).toUpperCase();
      (acc[letter] ||= []).push(entry);
      return acc;
    }, {});
  }, [query, activeCategory]);

  const letters = useMemo(
    () => Object.keys(grouped).sort((a, b) => a.localeCompare(b, "de")),
    [grouped],
  );
  const allActive = activeLetters();
  const totalResults = useMemo(
    () => letters.reduce((sum, l) => sum + grouped[l].length, 0),
    [letters, grouped],
  );
  const isFiltering = query.trim().length > 0 || activeCategory !== "Alle";


  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Finanz-Glossar – Begriffe verständlich erklärt | FINABLE"
        description="Von Abgeltungssteuer bis Zinseszinseffekt: Das FINABLE Finanz-Glossar erklärt die wichtigsten Begriffe rund um Altersvorsorge, ETFs, Steuern und Versicherungen verständlich."
        canonical="https://finable.de/glossar"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "DefinedTermSet",
          name: "FINABLE Finanz-Glossar",
          url: "https://finable.de/glossar",
          inLanguage: "de-DE",
          hasDefinedTerm: entries.map((e) => ({
            "@type": "DefinedTerm",
            "@id": `https://finable.de/glossar/${e.slug}`,
            name: e.term,
            description: e.short,
            url: `https://finable.de/glossar/${e.slug}`,
            inDefinedTermSet: "https://finable.de/glossar",
          })),
        }}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 md:pt-40 pb-16 md:pb-20 bg-gradient-hero text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial pointer-events-none" />
          <div className="container relative text-center max-w-3xl">
            <span className="reveal inline-block text-accent font-semibold uppercase tracking-wider text-sm">
              FINABLE Glossar
            </span>
            <h1 className="reveal mt-4 font-display text-4xl md:text-6xl font-extrabold text-balance leading-[1.05]">
              Finanzbegriffe <span className="text-accent">verständlich</span> erklärt
            </h1>
            <p className="reveal mt-6 text-lg md:text-xl text-white/80 leading-relaxed">
              Altersvorsorge, ETFs, Steuern und Versicherungen – die wichtigsten Begriffe rund um Deine Finanzen, klar und ohne Fachjargon.
            </p>

            {/* Search */}
            <div className="reveal mt-8 max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
              <label htmlFor="glossary-search" className="sr-only">
                Finanzbegriffe suchen
              </label>
              <input
                id="glossary-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Begriff suchen…"
                aria-label="Finanzbegriffe suchen"
                className="w-full pl-12 pr-4 py-3.5 rounded-full bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent backdrop-blur"
              />
            </div>

            {/* Category filter chips */}
            <div className="reveal mt-5 flex flex-wrap justify-center gap-2" role="group" aria-label="Nach Kategorie filtern">
              {(["Alle", ...CATEGORIES] as const).map((cat) => {
                const isActive = activeCategory === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setActiveCategory(cat)}
                    aria-pressed={isActive}
                    className={`px-3.5 h-8 inline-flex items-center rounded-full text-xs font-semibold uppercase tracking-wider transition-all border ${
                      isActive
                        ? "bg-accent text-accent-foreground border-accent"
                        : "bg-white/10 text-white/80 border-white/20 hover:bg-white/20 hover:text-white"
                    }`}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>

            {isFiltering && (
              <p className="reveal mt-4 text-sm text-white/70">
                {totalResults} {totalResults === 1 ? "Begriff" : "Begriffe"} gefunden
                {(query.trim() || activeCategory !== "Alle") && (
                  <>
                    {" · "}
                    <button
                      type="button"
                      onClick={() => {
                        setQuery("");
                        setActiveCategory("Alle");
                      }}
                      className="underline underline-offset-4 hover:text-accent transition-colors"
                    >
                      Filter zurücksetzen
                    </button>
                  </>
                )}
              </p>
            )}
          </div>
        </section>


        {/* A–Z navigation */}
        <section className="sticky top-20 z-30 bg-background/95 backdrop-blur border-b border-border">
          <div className="container py-4">
            <div className="flex flex-wrap justify-center gap-1.5 md:gap-2">
              {ALPHABET.map((l) => {
                const isActive = allActive.includes(l);
                return isActive ? (
                  <a
                    key={l}
                    href={`#letter-${l}`}
                    className="w-9 h-9 inline-flex items-center justify-center rounded-full text-sm font-bold text-primary bg-card border border-border hover:bg-accent hover:text-accent-foreground hover:border-accent transition-all"
                  >
                    {l}
                  </a>
                ) : (
                  <span
                    key={l}
                    className="w-9 h-9 inline-flex items-center justify-center rounded-full text-sm font-medium text-muted-foreground/40 cursor-not-allowed select-none"
                    aria-disabled="true"
                  >
                    {l}
                  </span>
                );
              })}
            </div>
          </div>
        </section>

        {/* Entries grouped by letter */}
        <section className="py-16 md:py-24 bg-background">
          <div className="container max-w-4xl">
            {letters.length === 0 && (
              <p className="text-center text-muted-foreground py-12">
                Keine Begriffe gefunden{query.trim() && <> für „{query}"</>}
                {activeCategory !== "Alle" && <> in Kategorie „{activeCategory}"</>}.
              </p>
            )}

            <div className="space-y-14">
              {letters.map((letter) => (
                <div key={letter} id={`letter-${letter}`} className="scroll-mt-40">
                  <div className="flex items-center gap-4 mb-6">
                    <h2 className="font-display text-5xl md:text-6xl font-extrabold text-accent leading-none">
                      {letter}
                    </h2>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                  <ul className="grid sm:grid-cols-2 gap-3">
                    {grouped[letter].map((entry) => (
                      <li key={entry.slug} className="reveal">
                        <Link
                          to={`/glossar/${entry.slug}`}
                          className="group flex items-start justify-between gap-3 p-4 rounded-xl border border-border bg-card hover:border-accent/40 hover:shadow-card transition-all"
                        >
                          <div>
                            <p className="font-display font-semibold text-primary group-hover:text-accent transition-colors">
                              {entry.term}
                            </p>
                            <p className="mt-1 text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                              {entry.short}
                            </p>
                          </div>
                          <ArrowRight className="w-4 h-4 mt-1 shrink-0 text-muted-foreground group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
